
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { applicationStoreGetter } from '@/store/application.store';
import Company from '@/models/Company';
import Ride from '@/models/Ride';
import Driver from '@/models/Driver';
import Taxi from '@/models/Taxi';
import Request from '@/models/Request';
import Phone from '@/interfaces/phone.interface';
import { rideStoreActions, rideStoreGetter } from '@/store/ride.store';
import moment from 'moment';
import BillingRepository from '@/api/repositories/BillingRepository';
import InvoiceRepository from '@/api/repositories/InvoiceRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import { downloadTablePdf, reportSubtitle } from '@/misc/pdf';
import { downloadTableXlsx, xlsxFileName } from '@/misc/xlsx';
import { ReportColumn, reportFileName } from '@/misc/report';

const ApplicationStore = namespace('application');
const RideStore = namespace('ride');

const billingRepository: BillingRepository = RepositoryFactory.get('billing');
const invoiceRepository: InvoiceRepository = RepositoryFactory.get('invoice');

@Component({
    components: {
        RideTableComponent: () => import(
            /* webpackChunkName: "RideTableComponent" */
            '@/components/RideTable.component.vue')
    }
})
export default class RidesView extends Vue {
    @ApplicationStore.Getter(applicationStoreGetter.SELECTED_COMPANY)
    public selectedCompany!: Company;

    @RideStore.Action(rideStoreActions.LIST)
    public loadAllRides!: (payload: { company: Company }) => Promise<Ride[]>;

    @RideStore.Getter(rideStoreGetter.RIDES)
    public allRides!: Ride[];

    public searchValue: string = '';

    public invoiceDialog: boolean = false;
    public isLoadingInvoice: boolean = false;
    public invoiceError: boolean = false;
    public invoice: any = null;

    private startMenu: boolean = false;
    private endMenu: boolean = false;
    private startDate: string | null = null;
    private endDate: string | null = null;

    public async created() {
        const today = moment(new Date());
        this.endDate = today.format('yyyy-MM-DD');
        today.set({ date: 1 });
        this.startDate = today.format('yyyy-MM-DD');
        await this.loadAllRides({ company: this.selectedCompany });
    }

    /**
     * The table shows every ride loaded for the selected company, but the date
     * pickers above it are part of this screen's controls, so the exports
     * honour them. That means an exported file can legitimately hold fewer
     * rows than the table below it.
     */
    private get ridesInRange(): Ride[] {
        if (!this.startDate || !this.endDate) {
            return this.allRides;
        }

        const from = moment(this.startDate, 'YYYY-MM-DD').startOf('day');
        const to = moment(this.endDate, 'YYYY-MM-DD').endOf('day');

        return this.allRides.filter((ride: Ride) => {
            if (!ride.start) return false;
            return moment(ride.start).isBetween(from, to, undefined, '[]');
        });
    }

    private get rideReportColumns(): ReportColumn<Ride>[] {
        return [
            { header: this.$t('RIDE.TABLE.DEPARTURE').toString(), value: (r) => this.request(r)?.departure },
            { header: this.$t('RIDE.TABLE.DESTINATION').toString(), value: (r) => this.request(r)?.destination },
            { header: this.$t('RIDE.TABLE.STARTTIME').toString(), value: (r) => this.toDate(r.start) },
            { header: this.$t('RIDE.TABLE.ENDTIME').toString(), value: (r) => this.toDate(r.end) },
            { header: this.$t('RIDE.TABLE.PRICE').toString(), value: (r) => r.offer?.price },
            { header: this.$t('RIDE.TABLE.PHONE').toString(), value: (r) => this.toPhone(this.customerPhone(r)) },
            { header: this.$t('RIDE.TABLE.DRIVER_NAME').toString(), value: (r) => this.toDriverName(r) },
            { header: this.$t('RIDE.TABLE.TAXI_NO').toString(), value: (r) => this.populated<Taxi>(r.driverTaxi?.taxi)?.taxiNo },
            { header: this.$t('RIDE.TABLE.DRIVER_NO').toString(), value: (r) => this.toPhone(this.driver(r)?.phone) },
            { header: this.$t('RIDE.TABLE.STATUS').toString(), value: (r) => r.offer?.status }
        ];
    }

    private async exportRidesXlsx(): Promise<void> {
        const rows = this.ridesInRange;
        if (!rows.length) {
            this.$notifyInfoSimplified('EXPORT.EMPTY');
            return;
        }

        await downloadTableXlsx({
            fileName: xlsxFileName('rides', this.selectedCompany?.name ?? null, this.startDate, this.endDate),
            sheetName: this.$t('EXPORT.TITLE.RIDES').toString(),
            rows,
            columns: this.rideReportColumns
        });
    }

    private async exportRidesPdf(): Promise<void> {
        const rows = this.ridesInRange;
        if (!rows.length) {
            this.$notifyInfoSimplified('EXPORT.EMPTY');
            return;
        }

        await downloadTablePdf({
            fileName: reportFileName('rides', this.selectedCompany?.name ?? null, this.startDate, this.endDate, 'pdf'),
            title: this.$t('EXPORT.TITLE.RIDES').toString(),
            subtitle: reportSubtitle(this.selectedCompany?.name ?? null, this.startDate, this.endDate),
            rows,
            columns: this.rideReportColumns
        });
    }

    /**
     * Handed to the exporters as a real `Date`, not a formatted string, so the
     * spreadsheet gets a genuine date cell it can size, sort and filter on.
     * The PDF flattens it back to text itself.
     */
    private toDate(value: Date | undefined): Date | undefined {
        if (!value) return undefined;
        const parsed = moment(value);
        return parsed.isValid() ? parsed.toDate() : undefined;
    }

    private toPhone(phone: Phone | undefined): string {
        if (!phone?.prefix || !phone?.phoneNumber) return '';
        return `${phone.prefix} ${phone.phoneNumber}`;
    }

    private toDriverName(ride: Ride): string {
        const driver = this.driver(ride);
        if (!driver) return '';
        return `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim();
    }

    /**
     * These models type populated references as `T | string`, because the API
     * returns a bare id whenever the document was not populated. Narrow to the
     * object form, or undefined when only an id came back — a row missing its
     * driver should export as blank, not as a raw ObjectId.
     */
    private populated<T>(value: T | string | undefined): T | undefined {
        return value && typeof value !== 'string' ? value as T : undefined;
    }

    private request(ride: Ride): Request | undefined {
        return this.populated<Request>(ride.offer?.request);
    }

    private driver(ride: Ride): Driver | undefined {
        return this.populated<Driver>(ride.driverTaxi?.driver);
    }

    /**
     * The Customer model does not declare `phone`, but the API sends it and the
     * rides table renders it. Reading it through a cast keeps this export in
     * step with the table without widening a shared model from here.
     */
    private customerPhone(ride: Ride): Phone | undefined {
        return (ride.customer as any)?.phone;
    }

    private async onGenerateRideOverview(): Promise<void> {
        if (!this.startDate || !this.endDate) {
            return;
        }

        const response = await billingRepository.generateCompanyPDF({
            company: this.selectedCompany.id,
            from: this.startDate,
            to: this.endDate
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download',
            `${this.selectedCompany.name}_${this.startDate}-${this.endDate}.pdf`);
        document.body.appendChild(link);
        link.click();
    }

    public async showInvoice(ride: Ride): Promise<void> {
        this.invoiceDialog = true;
        this.isLoadingInvoice = true;
        this.invoiceError = false;
        this.invoice = null;
        try {
            const response = await invoiceRepository.findByRide(ride.id);
            this.invoice = response.data;
        } catch (e) {
            this.invoiceError = true;
        } finally {
            this.isLoadingInvoice = false;
        }
    }
}
